import { useState } from "react";
import { SlotPicker } from "./SlotPicker";
import { sessionId } from "../lib/session";

const API = process.env.REACT_APP_BACKEND_URL;

export function WaitlistForm({ variant = "footer", className = "" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [trap, setTrap] = useState("");
  const [slot, setSlot] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(event) {
    event.preventDefault();
    if (!slot) {
      setStatus("error");
      setMessage("Pick a time for your demo first.");
      return;
    }
    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(`${API}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: name || undefined,
          company_website: trap || undefined,
          session_id: sessionId(),
          slot_start: slot?.start,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.ok === false) {
        setStatus("error");
        setMessage(data.error || "Something went wrong. Please try again.");
        if (response.status === 409 || response.status === 400) {
          setSlot(null);
          setRefreshKey((k) => k + 1);
        }
        return;
      }

      const when = data.slot_label
        ? `Your demo is booked for ${data.slot_label}.`
        : "We'll be in touch to schedule your demo.";
      setStatus("success");
      setMessage(
        `Got it. Lint is looking forward to meeting you. ${when}${data.confirmed ? " Check your inbox for a note from him." : ""}`
      );
      setEmail("");
      setName("");
    } catch {
      setStatus("error");
      setMessage("Network error. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <p
        className={`rounded-xl border border-teal/30 bg-teal/10 px-4 py-3 text-sm font-medium text-navy ${className}`}
        role="status"
        data-testid="waitlist-success"
      >
        {message}
      </p>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`flex w-full flex-col gap-3 mx-auto max-w-lg ${className}`}
      data-testid={`waitlist-form-${variant}`}
    >
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={trap}
        onChange={(e) => setTrap(e.target.value)}
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
        data-testid={`waitlist-honeypot-${variant}`}
      />
      <label className="sr-only" htmlFor="waitlist-name">
        Name
      </label>
      <input
          id="waitlist-name"
          type="text"
          name="name"
          autoComplete="name"
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none ring-brand/30 placeholder:text-ink-muted/70 focus:ring-2"
          data-testid="waitlist-name-input"
        />
      <SlotPicker value={slot} onChange={setSlot} refreshKey={refreshKey} />
      <div className="flex flex-col gap-3">
        <label className="sr-only" htmlFor={`waitlist-email-${variant}`}>
          Email
        </label>
        <input
          id={`waitlist-email-${variant}`}
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full flex-1 rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none ring-brand/30 placeholder:text-ink-muted/70 focus:ring-2"
          data-testid={`waitlist-email-input-${variant}`}
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex shrink-0 items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-cream transition-colors hover:bg-navy disabled:cursor-not-allowed disabled:opacity-70"
          data-testid={`waitlist-submit-${variant}`}
        >
          {status === "loading" ? "Booking…" : "Book a demo"}
        </button>
      </div>
      {status === "error" && (
        <p className="text-sm text-critical" role="alert" data-testid="waitlist-error">
          {message}
        </p>
      )}
    </form>
  );
}
